// Utilities for normalizing days/times and resolving TemplateType

const CANONICAL_DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DAY_SYNONYMS = {
  sun: 'Sun',
  sunday: 'Sun',
  mon: 'Mon',
  monday: 'Mon',
  tue: 'Tue',
  tues: 'Tue',
  tuesday: 'Tue',
  wed: 'Wed',
  weds: 'Wed',
  wednesday: 'Wed',
  thu: 'Thu',
  thur: 'Thu',
  thurs: 'Thu',
  thursday: 'Thu',
  fri: 'Fri',
  friday: 'Fri',
  sat: 'Sat',
  saturday: 'Sat',
};

export function normalizeDays(dayString) {
  if (!dayString || typeof dayString !== 'string')
    return { normalized: '', errors: ['Invalid day string'] };
  const tokens = dayString
    .split(/[,;]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  const normalizedSet = new Set();
  const errors = [];
  tokens.forEach((token) => {
    const key = token.toLowerCase();
    const label = DAY_SYNONYMS[key];
    if (!label) {
      errors.push(`Unknown day: ${token}`);
      return;
    }
    normalizedSet.add(label);
  });

  const ordered = CANONICAL_DAY_LABELS.filter((d) => normalizedSet.has(d));
  return { normalized: ordered.join(', '), errors };
}

export function isValidTimeHHMM(value) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value || '');
}

export function validateEnum(value, allowed) {
  return allowed.includes(value);
}

export function toInt(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

const ALLOWED_PRICE_DISPLAY = ['EXPLICIT', 'IMPLICIT'];

export function resolveTemplateType({ schemaType, discountUI }) {
  const raw = String(schemaType || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

  const MAP = {
    standard: 'Standard',
    expiring: 'ExpiringDiscount',
    expiringdiscount: 'ExpiringDiscount',
    subsidy: 'SubsidyDiscount',
    subsidydiscount: 'SubsidyDiscount',
    discount: 'Discount',
    mobilediscount: 'Discount',
    sharedsubsidy: 'SharedSubsidy',
  };

  const TIMEBOUND = new Set([
    'timebound',
    'timebounddiscount',
    'tmebound',
    'tmebounddiscount',
  ]);
  const TIMEBOUND_UI = new Set([
    'timebounddiscountui',
    'timeboundui',
    'tmebounddiscountui',
    'tmeboundui',
  ]);

  if (TIMEBOUND_UI.has(raw)) return 'TimeBoundDiscountUi';
  if (TIMEBOUND.has(raw))
    return discountUI ? 'TimeBoundDiscountUi' : 'TimeBoundDiscount';
  return MAP[raw] || null;
}

export function buildUpdateInputRow(csvRow, mode = 'update') {
  const {
    schemaId,
    schemaType,
    discount,
    priceDisplayType,
    platform,
    discountUI,
    replicaSchemaIdForBasePrices,
    sharedSubsidyClientDiscount,
  } = csvRow;
  const errors = [];
  const warnings = [];
  const templateType = resolveTemplateType({ schemaType, discountUI });
  const normalized = { schemaId, templateType };

  // Add replicaSchemaIdForBasePrices for Create mode
  if (mode === 'create' && replicaSchemaIdForBasePrices) {
    normalized.replicaSchemaIdForBasePrices = String(
      replicaSchemaIdForBasePrices,
    ).trim();
  }
  // Normalize platform once for potential use inside nested blocks only
  const p = (platform || '').toUpperCase();

  // Conditional priceDisplayType validation
  if (
    [
      'Discount',
      'SubsidyDiscount',
      'ExpiringDiscount',
      'TimeBoundDiscount',
      'TimeBoundDiscountUi',
      'SharedSubsidy',
    ].includes(templateType) &&
    !ALLOWED_PRICE_DISPLAY.includes(priceDisplayType)
  ) {
    errors.push('priceDisplayType must be EXPLICIT or IMPLICIT');
  }

  // Validate discount only for specific types
  if (
    [
      'Discount',
      'SubsidyDiscount',
      'ExpiringDiscount',
      'TimeBoundDiscount',
      'TimeBoundDiscountUi',
    ].includes(templateType)
  ) {
    const discountValue = parseFloat(discount);
    if (isNaN(discountValue) || discountValue < 1 || discountValue > 100) {
      errors.push('discount must be a number between 1 and 100');
    } else {
      // Assign discountValue to normalized object only if valid
      if (templateType === 'Discount') {
        normalized.discount = {
          discount: discountValue,
          priceDisplayType: priceDisplayType,
          platform: p === 'MOBILE' ? p : undefined,
        };
      } else if (templateType === 'SubsidyDiscount') {
        normalized.subsidy = {
          discount: discountValue,
          priceDisplayType: priceDisplayType,
        };
      } else if (templateType === 'ExpiringDiscount') {
        const days = toInt(csvRow.expiringDays);
        if (days === null || days <= 0) {
          errors.push('expiringDays must be a positive integer');
        } else {
          normalized.expiring = {
            expiringDays: days,
            discount: discountValue,
            priceDisplayType: priceDisplayType,
          };
        }
      } else if (
        templateType === 'TimeBoundDiscount' ||
        templateType === 'TimeBoundDiscountUi'
      ) {
        const schedule = String(csvRow.discountSchedule || '').trim();
        const timeWindows = [];
        if (!schedule) {
          errors.push(
            'timeBound.timeWindows is required and must be a non-empty list',
          );
        } else {
          // Split on ';' first; if none, also allow commas between blocks
          const blocks = schedule
            .split(/;+/)
            .filter(Boolean)
            .flatMap((b) => b.split(/,(?=\s*[A-Za-z])/))
            .map((b) => b.trim())
            .filter(Boolean);
          blocks.forEach((block) => {
            // Expect patterns like: Mon 15:00-17:00 or Tue 14:00–16:00
            const m = block.match(
              /^([A-Za-z]+)\s+([0-2]?\d:[0-5]\d)\s*[-–]\s*([0-2]?\d:[0-5]\d)$/,
            );
            if (!m) {
              errors.push(`Invalid time window: ${block}`);
              return;
            }
            const dayKey = m[1].toLowerCase();
            const dayLabel = DAY_SYNONYMS[dayKey];
            const startTime = m[2];
            const endTime = m[3];
            if (
              !dayLabel ||
              !isValidTimeHHMM(startTime) ||
              !isValidTimeHHMM(endTime)
            ) {
              errors.push(`Invalid time window: ${block}`);
              return;
            }
            timeWindows.push({ dayOfWeek: dayLabel, startTime, endTime });
          });
          if (timeWindows.length === 0) {
            errors.push(
              'timeBound.timeWindows is required and must be a non-empty list',
            );
          } else {
            normalized.timeBound = {
              discount: discountValue,
              priceDisplayType: priceDisplayType,
              timeWindows,
            };
          }
        }
      }
    }
  }

  // Handle SharedSubsidy template type
  if (templateType === 'SharedSubsidy') {
    const targetDiscount = parseFloat(discount);
    const clientSubsidy = parseFloat(sharedSubsidyClientDiscount);

    // Check if values are in valid range (1-100)
    const isTargetDiscountValid = targetDiscount >= 1 && targetDiscount <= 100;
    const isClientSubsidyValid = clientSubsidy >= 1 && clientSubsidy <= 100;

    // Validate targetDiscount (from discount column)
    if (!isTargetDiscountValid) {
      errors.push(
        'discount (targetDiscount) must be a number between 1 and 100',
      );
    }

    // Validate clientSubsidy (from sharedSubsidyClientDiscount column)
    if (!isClientSubsidyValid) {
      errors.push(
        'sharedSubsidyClientDiscount (clientSubsidy) must be a number between 1 and 100',
      );
    }

    // Validate that clientSubsidy is less than targetDiscount
    if (
      isTargetDiscountValid &&
      isClientSubsidyValid &&
      clientSubsidy >= targetDiscount
    ) {
      errors.push(
        'sharedSubsidyClientDiscount must be less than discount (targetDiscount)',
      );
    }

    // Build the sharedSubsidy object if all validations pass
    if (
      isTargetDiscountValid &&
      isClientSubsidyValid &&
      clientSubsidy < targetDiscount
    ) {
      normalized.sharedSubsidy = {
        targetDiscount: targetDiscount,
        clientSubsidy: clientSubsidy,
        priceDisplayType: priceDisplayType,
      };
    }
  }

  // Platform validation (do not set at root; only within discount block when valid)
  if (platform && p !== 'MOBILE') {
    errors.push('platform must be MOBILE or left blank');
  }

  // Non-blocking warnings for unused columns
  if (
    templateType !== 'ExpiringDiscount' &&
    String(csvRow.expiringDays || '').trim() !== ''
  ) {
    warnings.push('expiringDays is ignored for this template type');
  }
  if (
    !(
      templateType === 'TimeBoundDiscount' ||
      templateType === 'TimeBoundDiscountUi'
    ) &&
    String(csvRow.discountSchedule || '').trim() !== ''
  ) {
    warnings.push('discountSchedule is ignored for this template type');
  }
  if (
    templateType !== 'SharedSubsidy' &&
    String(csvRow.sharedSubsidyClientDiscount || '').trim() !== ''
  ) {
    warnings.push(
      'sharedSubsidyClientDiscount is ignored for this template type',
    );
  }

  // Attach warnings to errors array as non-blocking notes for display/download
  if (warnings.length) {
    errors.push(...warnings.map((w) => `(warning) ${w}`));
  }
  return { input: normalized, errors };
}

export function buildUpdateInputFromRows(rows, mode = 'update') {
  const results = [];
  const errorsByIndex = {};
  rows.forEach((row, idx) => {
    const { input, errors } = buildUpdateInputRow(row, mode);
    if (errors.length) {
      errorsByIndex[idx] = errors;
    }
    results.push({ ...input, schemaId: input.schemaId });
  });
  return { inputs: results, errorsByIndex };
}
